/*
 * ALTUKA personalization — accelerated checkout safety net.
 *
 * Horizon's own accelerated / dynamic checkout button (blocks/accelerated-checkout.liquid,
 * rendered via {{ form_obj | payment_button }}) is shown or hidden by
 * assets/product-form.js purely based on selected-variant availability. It is
 * NOT aware of custom required personalization fields (product-custom-property
 * or product-option-select blocks), so without this script a customer could
 * bypass required personalization by using Shop Pay / accelerated checkout
 * instead of the standard Add to Cart button.
 *
 * This script is purely additive: it does not modify or replace Horizon's own
 * product-form.js, product-custom-property.js, or product-option-select.js. It
 * only reads native form validity (HTMLFormElement.checkValidity(), which
 * already accounts for required fields associated with the form via the
 * `form="..."` attribute, whether or not they are physically nested inside
 * the <form> element) and, when invalid, hides the accelerated checkout
 * button the same way Horizon's own code does (the `hidden` attribute).
 *
 * It never forces the button to become visible — Horizon's own
 * availability-based logic continues to own that. A MutationObserver re-
 * asserts `hidden` if Horizon's own code removes it (e.g. after a variant
 * change) while personalization is still incomplete, so the two mechanisms
 * cannot race each other into showing the button prematurely.
 */
(function () {
  if (window.altukaPersonalizationGuardInitialized) return;
  window.altukaPersonalizationGuardInitialized = true;

  var ACCELERATED_CHECKOUT_SELECTOR = '.accelerated-checkout-block';
  var trackedForms = new Set();
  var trackedContainers = new WeakSet();

  function enforce(form) {
    if (!form) return;
    var valid = form.checkValidity();
    var containers = document.querySelectorAll(ACCELERATED_CHECKOUT_SELECTOR);
    containers.forEach(function (container) {
      if (!valid) {
        container.setAttribute('hidden', 'true');
        container.setAttribute('aria-disabled', 'true');
      } else {
        container.removeAttribute('aria-disabled');
        // Intentionally do not remove `hidden` here — Horizon's own
        // variant-availability logic owns showing the button again.
      }
    });
  }

  function watchContainer(container, form) {
    if (trackedContainers.has(container)) return;
    trackedContainers.add(container);

    var observer = new MutationObserver(function () {
      if (form && !form.checkValidity() && !container.hasAttribute('hidden')) {
        container.setAttribute('hidden', 'true');
        container.setAttribute('aria-disabled', 'true');
      }
    });
    observer.observe(container, { attributes: true, attributeFilter: ['hidden'] });
  }

  function trackForm(form) {
    if (!form || !form.id || trackedForms.has(form.id)) return;
    trackedForms.add(form.id);

    enforce(form);
    document.querySelectorAll(ACCELERATED_CHECKOUT_SELECTOR).forEach(function (container) {
      watchContainer(container, form);
    });

    var recheck = function () {
      enforce(form);
    };

    form.addEventListener('input', recheck);
    form.addEventListener('change', recheck);

    // Personalization inputs/selects are associated with the form via the
    // `form="..."` attribute rather than DOM nesting, so also listen at the
    // document level for events bubbling from any such field.
    document.addEventListener('input', function (evt) {
      var target = evt.target;
      if (target && target.getAttribute && target.getAttribute('form') === form.id) {
        recheck();
      }
    });
    document.addEventListener('change', function (evt) {
      var target = evt.target;
      if (target && target.getAttribute && target.getAttribute('form') === form.id) {
        recheck();
      }
    });
  }

  function init() {
    document.querySelectorAll('form[action*="/cart/add"][id]').forEach(trackForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Product media / variant sections can re-render after a variant change;
  // re-scan periodically-cheap mutations on the body for newly added forms.
  var bodyObserver = new MutationObserver(function () {
    document.querySelectorAll('form[action*="/cart/add"][id]').forEach(trackForm);
  });
  if (document.body) {
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }
})();